import vuetify from '@/plugins/vuetify';
import Vue from 'vue';
import getBrowserIdentity from '@/utils/browser-identity.js';
import { opDevFetch, OPError } from '@/utils/op-dev-fetch';


const rootURL = process.env.OP_DEV_CONFIG['service-url'].replace(/\/$/, '');

// For throttling requests
// const active = { t: null, n: 0 };

let refreshJWTPromise = null;

async function getRecaptchaTokenV2() {
  let resolveToken;
  const tokenPromise = new Promise(r => resolveToken = r);
  window.GLOBAL.resolveCaptchaV2 = resolveToken;
  try {
    await Promise.all([ loadRecaptchaPromise, Vue.nextTick() ]);
    await new Promise(ready => grecaptcha.ready(ready));
    grecaptcha.render(
      'captcha-container',
      {
        sitekey: process.env.OP_DEV_CONFIG['recaptcha-site-key-v2'],
        callback: resolveToken,
        'error-callback': () => resolveToken(null),
        'expired-callback': () => resolveToken(null)
      }
    );
    return await tokenPromise;
  } finally {
    window.GLOBAL.resolveCaptchaV2 = null;
  }
}

async function getRecaptchaTokenV3(action) {
  await loadRecaptchaPromise;
  return await new Promise(
    r => grecaptcha.ready(
      () => grecaptcha.execute(process.env.OP_DEV_CONFIG['recaptcha-site-key-v3'], { action }).then(r)
    )
  );
}

export default {
  methods: {
    async _promptReauth() {
      try {
        return await new Promise(r => this.global.resolveReauth = r);
      } catch (e) {
        console.error(e);
      } finally {
        this.global.resolveReauth = null;
      }
    },

    async _refreshJWTKernel() {
      // This function is effectively locked
      try {
        this.global.jwt = await opDevFetch(rootURL + '/get-jwt', undefined, { method: 'REFRESH', payload: this.global.jwt, browser: getBrowserIdentity() });
      } catch (e) {
        if ((e.type == 'ForceReauth') && (this.global.jwt = this._promptReauth()))
          return;
        if (e.type == 'AuthenticationException')
          this.global.jwt = null;
        if (e.type == 'InvalidTokenError')
          this.global.jwt = null;
        throw e;
      } finally {
        refreshJWTPromise = null;
      }
    },

    async $refreshJWT() {
      await (refreshJWTPromise ||= this._refreshJWTKernel());
    },

    async $call(fn, data=undefined, refresh=true) {
      const now = new Date();

      // Throttle requests
      // if (!active.t || (now - active.t) > 10000) {
      //   // The last request was made over 10 seconds ago, so we're good to go
      //   active.t = now;
      //   active.n = 0;
      // }
      // active.n++;
      // if (active.n >= 15) {
      //   // We've made at least 15 calls in the last ten seconds, slow down
      //   const delay = 10000 + (active.t - now);
      //   console.warn('Call-rate exceeded, throttling...');
      //   await new Promise(r => setTimeout(r, delay));
      //   return await this.$call(fn, data);
      // }

      if (refresh) {
        if (this.global.jwt && !this.global.profile)
          // This can happen if a call is made immediately
          await this.$nextTick();

        if (this.global.profile && this.global.profile.exp < (now.getTime() / 1000) + 10)
          // Our token is expired (or will expire within the next 10 seconds)
          try {
            await this.$refreshJWT();
          } catch (e) {}
      }

      const url = rootURL + '/' + fn.replace(/^\//, '');
      try {
        return await opDevFetch(url, this.global.jwt, data);
      } catch (e) {
        if (e instanceof OPError) {
          if (e.type == 'InvalidTokenSignature') {
            // Our current JWT is unusable with the backend (e.g. we may have forced re-auth)
            this.global.jwt = null;
            return await this.$call(fn, data);
          }
          if (refresh && (e.type == 'ExpiredToken')) {
            // This shouldn't happen often since we pre-emptively check for expired tokens
            try {
              await this.$refreshJWT();
            } catch (e2) {
              throw e;
            }
            return await opDevFetch(url, this.global.jwt, data);
          }
        }
        throw e;
      }
    },

    async $callWithCaptcha(fn, action, data=undefined, refresh=true) {
      // Injects the 'captcha' parameter into the data, automatically falling back to use V2 if required
      document.body.classList.add('grecaptcha-active');
      let captcha;
      try {
        captcha = await getRecaptchaTokenV3(action);
        return await this.$call(fn, { ...data, captcha, captcha_version: 'V3' }, refresh);
      } catch (e) {
        if (e.type == 'InvalidCaptcha') {
          captcha = await getRecaptchaTokenV2();
          if (!captcha)
            throw new VisibleError('Verification failed');
          return await this.$call(fn, { ...data, captcha, captcha_version: 'V2' }, refresh);
        }
        throw e;
      } finally {
        document.body.classList.remove('grecaptcha-active');
      }
    }
  }
};
